import { Dispatch, type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TAppThunk, TRootState } from 'slices/store';
import type { IDefaultReducerState } from 'types/state.interface';
import type { TLanguage } from '@timeedit/types/lib/types';
import api from '../services/api.service';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from '../utils/sliceHelpers';

// REDUX

type TLanguageState = IDefaultReducerState & {
  allLanguages: TLanguage[];
  dateFormats: TLanguage[];
};

type TPayloadLanguages = PayloadAction<{
  results: TLanguage[];
}>;

export const initialState: TLanguageState = {
  loading: false,
  hasErrors: false,
  allLanguages: [],
  dateFormats: [],
};

// Slice
const slice = createSlice({
  name: 'languages',
  initialState,
  reducers: {
    fetchLanguagesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchLanguagesSuccess: (state, { payload }: TPayloadLanguages) => {
      state.allLanguages = payload.results;
      finishedLoadingSuccess(state);
    },
    fetchDateFormatsSuccess: (state, { payload }: TPayloadLanguages) => {
      state.dateFormats = payload.results;
      finishedLoadingSuccess(state);
    },
    fetchLanguagesFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const languagesLoading = (state: TRootState) => state.languages.loading;
export const languagesSelector = (state: TRootState) =>
  state.languages.allLanguages;
export const dateFormatsSelector = (state: TRootState) =>
  state.languages.dateFormats;

// Actions
export const {
  fetchLanguagesRequest,
  fetchLanguagesSuccess,
  fetchLanguagesFailure,
  fetchDateFormatsSuccess,
} = slice.actions;

export const fetchLanguages = (): TAppThunk => async (dispatch) => {
  try {
    dispatch(fetchLanguagesRequest());
    const languages = await api.get({ endpoint: `/languages` });
    dispatch(fetchLanguagesSuccess(languages));
  } catch (e) {
    dispatch(fetchLanguagesFailure());
    return console.error(e);
  }
};

export const fetchDateFormats = (): TAppThunk => async (dispatch) => {
  try {
    dispatch(fetchLanguagesRequest());
    const languages = await api.get({
      endpoint: `/languages?type=DATE_FORMAT`,
    });
    dispatch(fetchDateFormatsSuccess(languages));
  } catch (e) {
    dispatch(fetchLanguagesFailure());
    return console.error(e);
  }
};
